// frontend/src/pages/AdminTrashPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Admin Trash / Restore Management — Phase 4 of the Platform Soft-Delete Migration.
//
// Provides Platform Admins (r1/r2) with a centralised UI to:
//   - View a summary dashboard of all deleted records platform-wide
//   - Browse entity tables that support soft-delete
//   - View individual deleted records (with name, deleted_at, deleted_by)
//   - Restore individual records
//   - Permanently purge individual records (hard delete)
//   - Bulk-purge all records past the retention window
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api/index.js';
import {
  PageHeader,
  Button,
  LoadingSpinner,
  ConfirmModal,
  StandardPageLayout,
} from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { formatDateTime } from '../utils/dateTime';
import { notify } from '@/components/core';

// ─── API helpers ────────────────────────────────────────────────────────────
const TRASH_BASE = '/admin/trash';

const fetchSummary     = (token) => apiFetch(`${TRASH_BASE}/summary`, token);
const fetchTables      = (token) => apiFetch(`${TRASH_BASE}/tables`, token);
const fetchItems       = (token, table, page = 1, pageSize = 50) =>
  apiFetch(`${TRASH_BASE}/items?table=${encodeURIComponent(table)}&page=${page}&page_size=${pageSize}`, token);
const restoreRecord    = (token, table, recordId) =>
  apiFetch(`${TRASH_BASE}/restore`, token, { method: 'POST', body: JSON.stringify({ table, record_id: recordId }) });
const purgeRecord      = (token, table, recordId) =>
  apiFetch(`${TRASH_BASE}/purge`, token, { method: 'POST', body: JSON.stringify({ table, record_id: recordId }) });
const purgeExpiredRecords = (token, table = null, retentionDays = 30) =>
  apiFetch(`${TRASH_BASE}/purge-expired`, token, { method: 'POST', body: JSON.stringify({ table, retention_days: retentionDays }) });


// ─── SummaryCard ────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, accent = 'blue', sub }) => (
  <div className={`relative overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700
      bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 p-6 group`}>
    {/* Subtle gradient accent bar */}
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${
      accent === 'blue'   ? 'from-blue-500 to-cyan-400'   :
      accent === 'amber'  ? 'from-amber-500 to-orange-400' :
      accent === 'red'    ? 'from-red-500 to-rose-400'     :
      accent === 'purple' ? 'from-purple-500 to-violet-400' :
      'from-gray-400 to-gray-300'
    }`} />
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${
        accent === 'blue'   ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'   :
        accent === 'amber'  ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
        accent === 'red'    ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'     :
        accent === 'purple' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' :
        'bg-gray-50 dark:bg-gray-700 text-gray-600'
      } transition-transform group-hover:scale-110`}>
        <DynamicIcon name={icon} className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">{label}</p>
        <p className="text-3xl font-extrabold text-gray-900 dark:text-white tabular-nums">{value}</p>
        {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p>}
      </div>
    </div>
  </div>
);


// ─── TableRow ───────────────────────────────────────────────────────────────
const TableRow = ({ table, label, count, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
      ${isSelected
        ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 shadow-sm'
        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 border border-transparent'}
    `}
  >
    <div className="flex items-center gap-3">
      <div className={`w-2 h-2 rounded-full ${count > 0 ? 'bg-amber-500 animate-pulse' : 'bg-green-500'}`} />
      <span className={`text-sm font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
        {label}
      </span>
    </div>
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
      count > 0
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
        : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
    }`}>
      {count}
    </span>
  </button>
);


// ─── ItemRow ────────────────────────────────────────────────────────────────
const ItemRow = ({ item, onRestore, onPurge }) => {
  const deletedDate = item.deleted_at
    ? formatDateTime(item.deleted_at)
    : '—';
  const itemName = item.name || item.id;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50/60 dark:hover:bg-gray-700/20 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{itemName}</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <DynamicIcon name="Calendar" className="w-3 h-3" /> {deletedDate}
          </span>
          {item.deleted_by && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <DynamicIcon name="User" className="w-3 h-3" /> {item.deleted_by}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4 shrink-0">
        <button
          onClick={() => onRestore(item)}
          title="Restore"
          className="p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 transition-colors"
        >
          <DynamicIcon name="RotateCcw" className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPurge(item)}
          title="Permanently Delete"
          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 transition-colors"
        >
          <DynamicIcon name="Trash2" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};


// ─── AdminTrashPage ─────────────────────────────────────────────────────────
const AdminTrashPage = ({ token }) => {

  // ---------- State ----------
  const [loading, setLoading]           = useState(true);
  const [summary, setSummary]           = useState(null);
  const [tables, setTables]             = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [items, setItems]               = useState([]);
  const [itemsMeta, setItemsMeta]       = useState({ total: 0, page: 1, page_size: 50 });
  const [itemsLoading, setItemsLoading] = useState(false);

  // Confirm modals
  const [restoreTarget, setRestoreTarget]       = useState(null);
  const [purgeTarget, setPurgeTarget]           = useState(null);
  const [showPurgeExpired, setShowPurgeExpired] = useState(false);
  const [purging, setPurging]                   = useState(false);

  // ---------- Data fetching ----------
  const loadSummaryAndTables = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([fetchSummary(token), fetchTables(token)]);
      setSummary(s);
      setTables(t);
    } catch (err) {
      notify.error(err?.message || 'Failed to load trash data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadItems = useCallback(async (table, page = 1) => {
    setItemsLoading(true);
    try {
      const data = await fetchItems(token, table, page);
      setItems(data.items || []);
      setItemsMeta({ total: data.total || 0, page: data.page || 1, page_size: data.page_size || 50 });
    } catch (err) {
      notify.error(err?.message || 'Failed to load items');
    } finally {
      setItemsLoading(false);
    }
  }, [token]);

  useEffect(() => { loadSummaryAndTables(); }, [loadSummaryAndTables]);

  useEffect(() => {
    if (selectedTable) loadItems(selectedTable);
    else { setItems([]); setItemsMeta({ total: 0, page: 1, page_size: 50 }); }
  }, [selectedTable, loadItems]);

  // ---------- Actions ----------
  const handleRestore = async () => {
    if (!restoreTarget || !selectedTable) return;
    try {
      await restoreRecord(token, selectedTable, restoreTarget.id);
      notify.success(`Restored "${restoreTarget.name || restoreTarget.id}" successfully.`);
      loadItems(selectedTable, itemsMeta.page);
      loadSummaryAndTables();
    } catch (err) {
      notify.error(err?.message || 'Restore failed');
    } finally {
      setRestoreTarget(null);
    }
  };

  const handlePurge = async () => {
    if (!purgeTarget || !selectedTable) return;
    try {
      await purgeRecord(token, selectedTable, purgeTarget.id);
      notify.success(`Permanently deleted "${purgeTarget.name || purgeTarget.id}".`);
      loadItems(selectedTable, itemsMeta.page);
      loadSummaryAndTables();
    } catch (err) {
      notify.error(err?.message || 'Purge failed');
    } finally {
      setPurgeTarget(null);
    }
  };

  const handlePurgeExpired = async () => {
    setPurging(true);
    try {
      const result = await purgeExpiredRecords(token);
      notify.error(result.message || 'Expired records purged.');
      loadSummaryAndTables();
      if (selectedTable) loadItems(selectedTable, 1);
    } catch (err) {
      notify.error(err?.message || 'Purge expired failed');
    } finally {
      setPurging(false);
      setShowPurgeExpired(false);
    }
  };

  // ---------- Pagination ----------
  const totalPages = Math.max(1, Math.ceil(itemsMeta.total / itemsMeta.page_size));
  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    loadItems(selectedTable, p);
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <span className="ml-3 text-gray-500">Loading trash data…</span>
      </div>
    );
  }

  const selectedMeta = tables.find(t => t.table === selectedTable);

  return (
    <StandardPageLayout
      title="Trash Management"
      description="Browse, restore, and permanently purge soft-deleted records across the platform."
      headerActions={
        <Button
          variant="secondary"
          onClick={() => setShowPurgeExpired(true)}
          disabled={!summary?.total_eligible_for_purge}
        >
          <DynamicIcon name="Timer" className="w-4 h-4 mr-2" />
          Purge Expired ({summary?.total_eligible_for_purge || 0})
        </Button>
      }
    >

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon="Trash2"
          label="Total Deleted"
          value={summary?.total_deleted?.toLocaleString() || '0'}
          accent="amber"
          sub="Across all entity tables"
        />
        <SummaryCard
          icon="AlertTriangle"
          label="Eligible for Purge"
          value={summary?.total_eligible_for_purge?.toLocaleString() || '0'}
          accent="red"
          sub={`Older than ${summary?.retention_days || 30} days`}
        />
        <SummaryCard
          icon="Database"
          label="Tables Tracked"
          value={summary?.tables_with_soft_delete || '0'}
          accent="blue"
          sub="Entity tables with soft-delete"
        />
        <SummaryCard
          icon="Clock"
          label="Retention Period"
          value={`${summary?.retention_days || 30}d`}
          accent="purple"
          sub="Auto-purge after this period"
        />
      </div>

      {/* Main Content: Table Browser + Item List */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Table List */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <DynamicIcon name="Layers" className="w-4 h-4" />
                Entity Tables
              </h3>
            </div>
            <div className="p-2 max-h-[600px] overflow-y-auto space-y-0.5">
              {tables.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No tables configured</p>
              ) : (
                tables.map(t => (
                  <TableRow
                    key={t.table}
                    table={t.table}
                    label={t.label}
                    count={t.deleted_count}
                    isSelected={selectedTable === t.table}
                    onClick={() => setSelectedTable(selectedTable === t.table ? null : t.table)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Item List */}
        <div className="lg:col-span-8 xl:col-span-9">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden min-h-[400px]">
            {!selectedTable ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                  <DynamicIcon name="MousePointerClick" className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-1">Select a Table</h3>
                <p className="text-sm text-gray-400 max-w-xs">
                  Click on an entity table from the left panel to browse its deleted records.
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <DynamicIcon name="Trash2" className="w-4 h-4 text-amber-500" />
                      {selectedMeta?.label || selectedTable}
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {itemsMeta.total} deleted record{itemsMeta.total !== 1 ? 's' : ''} · Table: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{selectedTable}</code>
                    </p>
                  </div>
                  <button
                    onClick={() => loadItems(selectedTable, itemsMeta.page)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                    title="Refresh"
                  >
                    <DynamicIcon name="RefreshCw" className="w-4 h-4" />
                  </button>
                </div>

                {/* Items */}
                {itemsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <LoadingSpinner />
                  </div>
                ) : items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center mb-3">
                      <DynamicIcon name="CheckCircle" className="w-7 h-7 text-green-500" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No deleted records</p>
                    <p className="text-xs text-gray-400 mt-1">This table has no soft-deleted items.</p>
                  </div>
                ) : (
                  <div>
                    {items.map((item, idx) => (
                      <ItemRow
                        key={item.id || idx}
                        item={item}
                        onRestore={setRestoreTarget}
                        onPurge={setPurgeTarget}
                      />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                    <p className="text-xs text-gray-400">
                      Page {itemsMeta.page} of {totalPages} · {itemsMeta.total} records
                    </p>
                    <div className="flex gap-1">
                      <button
                        disabled={itemsMeta.page <= 1}
                        onClick={() => goToPage(itemsMeta.page - 1)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        disabled={itemsMeta.page >= totalPages}
                        onClick={() => goToPage(itemsMeta.page + 1)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Confirmation Modals ─────────────────────────────────────────── */}

      {/* Restore Confirmation */}
      <ConfirmModal
        isOpen={!!restoreTarget}
        title="Restore Record"
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleRestore}
      >
        <p className="text-gray-600 dark:text-gray-300">
          Are you sure you want to restore <strong>"{restoreTarget?.name || restoreTarget?.id}"</strong> from{' '}
          <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{selectedTable}</code>?
        </p>
        <p className="text-xs text-gray-400 mt-2">The record will become active again and visible to users.</p>
      </ConfirmModal>

      {/* Purge Confirmation */}
      <ConfirmModal
        isOpen={!!purgeTarget}
        title="Permanently Delete Record"
        onClose={() => setPurgeTarget(null)}
        onConfirm={handlePurge}
      >
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-300 font-medium flex items-center gap-2">
            <DynamicIcon name="AlertTriangle" className="w-4 h-4" />
            This action cannot be undone!
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-300">
          Permanently delete <strong>"{purgeTarget?.name || purgeTarget?.id}"</strong> from{' '}
          <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{selectedTable}</code>?
        </p>
      </ConfirmModal>

      {/* Purge Expired Confirmation */}
      <ConfirmModal
        isOpen={showPurgeExpired}
        title="Purge All Expired Records"
        onClose={() => setShowPurgeExpired(false)}
        onConfirm={handlePurgeExpired}
      >
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-300 font-medium flex items-center gap-2">
            <DynamicIcon name="AlertTriangle" className="w-4 h-4" />
            This action cannot be undone!
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-300">
          This will permanently delete <strong>{summary?.total_eligible_for_purge || 0} record(s)</strong> across all entity tables
          that have been in the trash for more than <strong>{summary?.retention_days || 30} days</strong>.
        </p>
        {purging && (
          <div className="flex items-center gap-2 mt-3 text-amber-600">
            <LoadingSpinner size="sm" /> <span className="text-sm">Purging…</span>
          </div>
        )}
      </ConfirmModal>
    </StandardPageLayout>
  );
};

export default AdminTrashPage;
